import React from 'react';
import markdownit from 'markdown-it'

import { FontAwesomeIcon, PracticeSetIcon, VideoClipIcon } from '@magoosh/lib/icons';
import { paths } from 'config/path_helpers';
import { renderLatexInRawHtml } from 'utilities/latex';
import { pluralize } from 'utilities/pluralize';
import { secondsFormattedAsMinutes } from 'utilities/time';

import { InteractionPart } from '@test_prep/app/ai_tutor/types';

interface Props {
  interactionPart: InteractionPart;
}

export default (props: Props) => {
  const textWithLatex = () => {
    let text = props.interactionPart.content.text;

    (text.match(/\\\((.+?)\\\)/gs) || []).forEach((match) => {
      const latexSource = match.replace('\\\(', '').replace('\\\)', '').replace('\n', '');
      text = text.replace(match, `<code class="redactor-katex" data-source="${latexSource}"></code>`);
    });

    (text.match(/\\\[(.+?)\\\]/gs) || []).forEach((match) => {
      const latexSource = match.replace('\\\[', '').replace('\\\]', '').replace(/\n/g, '');
      text = text.replace(match, `<code class="redactor-katex" data-source="${latexSource}"></code>`);
    });

    return renderLatexInRawHtml(text);
  }

  const html = (text) => {
    const md = markdownit({ html: true });
    return md.render(text);
  }

  return (
    <div className="d-flex flex-column gap-s">
      <div dangerouslySetInnerHTML={{ __html: html(textWithLatex()) }} />

      <div className="d-flex flex-column gap-s">
        {
          props.interactionPart.content.offeredContent.map((content, idx) => {
            return (
              <React.Fragment key={idx}>
                {(() => {
                  if (content.type === 'Lesson') {
                    return <Lesson {...content} />;
                  } else if (content.type === 'PracticeSession') {
                    return <PracticeSession {...content} />;
                  }
                })()}

                {
                  idx !== props.interactionPart.content.offeredContent.length - 1 && (
                    <hr className="u-margin-A-n" />
                  )
                }
              </React.Fragment>
            );
          })
        }
      </div>
    </div>
  );
};

interface LessonProps {
  id: number;
  lengthInSeconds: number;
  locked: boolean;
  name: string;
  type: 'Lesson';
  watched: boolean;
}

const Lesson = (props: LessonProps) => {
  return (
    <a className="text-no-decoration text-hover-no-decoration" href={paths.lesson(props.id)}>
      <div className="align-items-center d-flex gap-s">
        <VideoClipIcon />

        <div className="d-flex flex-column">
          <div className="text-body">
            <strong>{props.name}</strong>{' '}
            {
              props.locked && (
                <FontAwesomeIcon className="text-gray-light" icon="fa-solid fa-lock" />
              )
            }
          </div>

          <div className="small text-gray-light">
            {
              props.watched ? (
                'Watched previously'
              ) : (
                `${secondsFormattedAsMinutes(Math.round(props.lengthInSeconds / 60) * 60)} minutes`
              )
            }
          </div>
        </div>
      </div>
    </a>
  );
};

interface PracticeSessionProps {
  completed: boolean;
  id: number;
  name: string;
  params: {
    difficultyMax: number;
    difficultyMin: number;
    examSectionId: number;
    includeOldPrompts: boolean;
    // Older data does not include this param
    practiceGroupType?: string;
    promptCountLimit: number;
    quizMode: boolean;
    rootExamSectionId: number;
    source: string;
    tagIds: number[];
  };
  type: 'PracticeSession';
}

const PracticeSession = (props: PracticeSessionProps) => {
  const practiceSessionParams = ({ tagsAsArray }) => {
    const params = [
      ['section', props.params.rootExamSectionId],
      ['practice_session[advanced_options_str]', props.params.includeOldPrompts ? 'all' : 'default'],
      ['practice_session[custom_practice]', true],
      ['practice_session[exam_section_id]', props.params.examSectionId],
      ['practice_session[prompt_count_limit]', props.params.promptCountLimit],
      ['practice_session[quiz_mode]', props.params.quizMode],
      ['practice_session[source]', props.params.source]
    ];

    if (props.params.difficultyMin && props.params.difficultyMax) {
      params.push([
        'practice_session[difficulty_range_str]',
        [props.params.difficultyMin, props.params.difficultyMax].join(',')
      ]);
    }

    if (tagsAsArray) {
      props.params.tagIds.forEach((tagId) => (
        params.push(['practice_session[tag_ids][]', tagId])
      ));
    } else {
      params.push(['practice_session[tag_ids]', props.params.tagIds.join(',')]);
    }

    return params;
  };

  const description = () => {
    let difficultyString = null;

    if (props.params.difficultyMin === -99 && props.params.difficultyMax === -1) {
      difficultyString = 'easy';
    } else if (props.params.difficultyMin === -1 && props.params.difficultyMax === 0) {
      difficultyString = 'medium';
    } else if (props.params.difficultyMin === 0 && props.params.difficultyMax === 1) {
      difficultyString = 'hard';
    } else if (props.params.difficultyMin === 1 && props.params.difficultyMax === 99) {
      difficultyString = 'very hard';
    }

    const practiceGroupType = props.params.practiceGroupType || 'question';

    if (difficultyString) {
      return pluralize(props.params.promptCountLimit, `${difficultyString} ${practiceGroupType}`);
    } else {
      return pluralize(props.params.promptCountLimit, practiceGroupType);
    }
  };

  return (
    <a
      className="text-no-decoration text-hover-no-decoration"
      href={
        props.completed
          ? paths.answers({ 'review[practice_session_id]': props.id })
          : paths.startQuickPractice(practiceSessionParams({ tagsAsArray: true }))}
    >
      <div className="align-items-center d-flex gap-s">
        <PracticeSetIcon />

        <div className="d-flex flex-1 flex-column">
          <div className="text-body">
            <strong>{props.name}</strong>{' '}
          </div>

          <div className="small text-gray-light">
            {
              props.completed ? (
                <>
                  Completed
                  {' • '}
                  <a
                    className="text-gray-light"
                    href={paths.answers({ 'review[practice_session_id]': props.id })}
                  >
                    Review
                  </a>
                </>
              ) : (
                <>
                  {description()}
                  {' • '}
                  <a
                    className="text-gray-light"
                    href={paths.startPractice(practiceSessionParams({ tagsAsArray: false }))}
                  >
                    Customize
                  </a>
                </>
              )
            }
          </div>
        </div>
      </div>
    </a>
  );
};
